<template>
  <Sidebar />
  <Content class="analytics-product-content analytics-big-box">
    <div class="inner inner-box">
      <div style="height: calc(100% - 0px)">
        <AnalyticsHeader
          v-if="store.state.user_data.merchant_account_id"
          :data_statistic="data_statistic"
          @handleSortLink="sortLinkClick"
        />

        <Scrollbar class="left" id="body">
          <!-- 要在show_sortlink=true的狀況下，判定last_time要不要在取代他 -->
          <ProductList
            id="scroll-left"
            :title="'All Products'"
            :is_loading="is_loading"
            :data="data_products"
            :date_range="{ start_date: start_date, end_date: end_date }"
            :sort_index="product_list_sort_index"
            :sort="sort"
            @update-sort="getProductList"
            :is_loading_page="is_loading_products_page"
            :show_sortlink="true"
            :data_statistic="data_statistic"
            :is_show_brand_dropdown="true"
            :current_page="current_page"
            :total_page="last_page"
            :total="total"
            @handleSortLink="sortLinkClick"
            :show_last_datetime="true"
            :init_sortlink="sortlink_data"
            :key="sortlink_data"
            :is_all_products="true"
          />
        </Scrollbar>
      </div>
    </div>
  </Content>
</template>

<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Sidebar from '@/components/Sidebar.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import AnalyticsHeader from '@/components/analytics/AnalyticsHeader.vue';
import Content from '@/components/ui/Content.vue';
import ProductList from '@/components/product/ProductList.vue';
import moment from 'moment';
import { SwitchSort, handleMaxMinFun, dataSourceToApi } from '@/utils/SwitchFun';

const store = useStore();
const router = useRouter();
const { proxy } = getCurrentInstance();
const $ajax = proxy.$ajax;
const EventBus = proxy.EventBus;

// Data
const start_date = ref(moment().add(-30, 'days').toDate());
const end_date = ref(moment().add(-1, 'days').toDate());
const is_loading = ref(true);
const is_loading_products_page = ref(false);
const is_loading_products = ref(true);
const product_list_sort_index = ref(0);
const data_statistic = ref(false);
const data_products = ref([]);
const current_page = ref(1);
const last_page = ref(1);
const total = ref(0);
const sort = reactive({
  key: 'views',
  val: 'desc',
});
const sortlink_data = ref('all');
const brand_list = ref([]);
const curated_check = ref(false);
const keyword = ref('');
const selected_creator_tags = ref([]);
const creator_tag_operation_type = ref('or');

//computed
const is_link_only = computed(() => {
  return store.state.user_data?.merchant_account?.platform_type === 'linkonly';
});

// Watch
watch(
  () => store.state.user_data.merchant_account_id,
  () => {
    startFunction();
  }
);

// Methods
const sortLinkClick = (val) => {
  sortlink_data.value = val;
  getStatistic();
  getProductList();
};

const getApiParamStr = (api) => {
  let s = moment(start_date.value).format('YYYY-MM-DD');
  let e = moment(end_date.value).format('YYYY-MM-DD');
  return `${api}?start_date=${s}&end_date=${e}`;
};

const getStatistic = () => {
  if (store.state.user_data.merchant_account_id) {
    let api = `/analytics/merchant-account-statistic`;

    // 因為不管如何我都要先知道有sortlink=1的時候有沒有資料，所以這邊強行都CALL 1，但顯示不顯示由前端操控
    api =
      getApiParamStr(api) +
      `&is_include_shortlink_data=1&product_keyword=${keyword.value}&is_include_curation_data=${
        curated_check.value ? '1' : '0'
      }`;

    if (brand_list.value.length > 0) {
      // brand_ids[]
      brand_list.value.forEach((brand) => {
        api += `&brand_ids[]=${brand.id}`;
      });
    }

    // Add creator tag filters
    if (selected_creator_tags.value.length > 0) {
      selected_creator_tags.value.forEach((tag) => {
        api += `&creator_tag_ids[]=${tag.id}`;
      });
      api += `&operation_type=${creator_tag_operation_type.value}`;
    }

    $ajax(api, {
      method: 'get',
      withCredentials: true,
    }).then((res) => {
      data_statistic.value = res.data.data;
    });
  }
};

const getProductList = (obj) => {
  if (store.state.user_data.merchant_account_id) {
    is_loading.value = true;
    is_loading_products.value = true;

    if (obj?.curated_check !== undefined) {
      curated_check.value = obj.curated_check;
    }

    keyword.value = obj ? obj.keyword : keyword.value;
    if (document.getElementById('body'))
      document.getElementById('body').scrollTop = document.getElementById('body').scrollTop - 50;
    let api = `/creator/product-list`;

    api =
      getApiParamStr(api) +
      `&page=${current_page.value}&sort_key=${sort.key}&sort_val=${sort.val}&is_include_shortlink_data=${
        dataSourceToApi(sortlink_data.value)
      }&product_keyword=${keyword.value}&is_include_curation_data=${curated_check.value ? '1' : '0'}`;

    if (obj) {
      product_list_sort_index.value = obj.type.id - 1;

      let sw_val = SwitchSort(obj.type.id);
      if (sw_val.key) {
        sort.key = sw_val.key;
        sort.val = sw_val.val;
      }
      current_page.value = 1;
      let newApi =
        `/creator/product-list?start_date=${obj.date.start}&end_date=${obj.date.end}` +
        `&page=${current_page.value}&is_include_shortlink_data=${dataSourceToApi(sortlink_data.value)}&is_include_curation_data=${
          curated_check.value ? '1' : '0'
        }`;
      api = `${newApi}&sort_key=${sort.key}&sort_val=${sort.val}&product_keyword=${keyword.value}`;
      getStatistic();
    }

    if (current_page.value > 1) is_loading_products_page.value = true;

    if (brand_list.value.length > 0) {
      // brand_ids[]
      brand_list.value.forEach((brand) => {
        api += `&brand_ids[]=${brand.id}`;
      });
    }

    // Add creator tag filters
    if (selected_creator_tags.value.length > 0) {
      selected_creator_tags.value.forEach((tag) => {
        api += `&creator_tag_ids[]=${tag.id}`;
      });
      api += `&operation_type=${creator_tag_operation_type.value}`;
    }

    $ajax(api, {
      method: 'get',
      withCredentials: true,
    }).then((res) => {
      if (current_page.value === 1) data_products.value = res.data.data;
      else
        res.data.data.forEach((p) => {
          data_products.value.push(p);
        });
      current_page.value = Number(res.data.current_page);
      last_page.value = Number(res.data.last_page);
      total.value = Number(res.data.total);
      nextTick(() => {
        is_loading_products.value = false;
        is_loading.value = false;
        is_loading_products_page.value = false;
      });
    });
  }
};

const handleScroll = () => {
  let body = document.getElementById('body').scrollTop + 1000;
  let scroll = document.getElementById('scroll-left').offsetHeight;

  if (body > scroll && !is_loading_products.value && current_page.value < last_page.value) {
    current_page.value++;
    getProductList();
  }
};

const startFunction = () => {
  sortlink_data.value = store.state.save_sortlink_data;

  // Initialize dates from store if available
  if (store.state.save_analytics_start_date && store.state.save_analytics_end_date) {
    start_date.value = store.state.save_analytics_start_date;
    end_date.value = store.state.save_analytics_end_date;
  }

  let checkTime = handleMaxMinFun(start_date.value, end_date.value, proxy);
  start_date.value = checkTime[0];
  end_date.value = checkTime[1];
  sort.key = !is_link_only.value ? 'views' : 'click';
  getStatistic();
  getProductList();
};

// Lifecycle hooks
onMounted(() => {
  if (!store.state.user_data.permission_list.analytics.viewPerformanceSummaries.permission) {
    router.push('/creator/list');
    return;
  }
  // Initialize creator tags from store BEFORE startFunction (so API calls include tags)
  if (store.state.save_selected_tags && store.state.save_selected_tags.length > 0) {
    selected_creator_tags.value = store.state.save_selected_tags;
    creator_tag_operation_type.value = store.state.save_tag_operation_type || 'or';
  }

  startFunction();
  store.state.creator_data = null;

  EventBus.off('update-date');
  EventBus.on('update-date', (obj) => {
    start_date.value = obj.start_date;
    end_date.value = obj.end_date;
    current_page.value = 1;
    brand_list.value = [];

    setTimeout(() => {
      getStatistic();
      getProductList();
    }, 10);
  });

  EventBus.on('update-by-brands', (obj) => {
    brand_list.value = obj;
    current_page.value = 1;
    getStatistic();
    getProductList();
  });

  // Listen for creator tag updates
  EventBus.off('analytics-tags-updated');
  EventBus.on('analytics-tags-updated', (data) => {
    console.log('!analytics-tags-updated!', data);
    selected_creator_tags.value = data.tags;
    creator_tag_operation_type.value = data.operation_type;
    current_page.value = 1;
    getStatistic();
    getProductList();
  });

  document.getElementById('body').addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  document.getElementById('body').removeEventListener('scroll', handleScroll);
  EventBus.off('analytics-tags-updated');
});
</script>

<style lang="scss">
.analytics-product-content {
  .inner {
    height: 100%;
    padding: 24px 48px; //24px 24px 0 24px;

    .list-header {
      .ui-dropdown {
        margin-right: 0;
      }
    }

    .left {
      width: 100%;
      //min-width: 600px;
      overflow-x: hidden;
      height: calc(100% - 150px);

      .product-list {
        margin-top: 36px;
      }

      .ui-product-list {
        padding-bottom: 24px;

        // Fix gap by adjusting sticky positioning (following commit b9a21d39)
        .list-header {
          top: 0; // Changed from 13px to remove gap
          padding-top: 0; // Reduce spacing to match Collections tab
        }
      }
    }
  }
}
</style>
